import React, { useState } from 'react';

/**
 * Social Post Card Component
 * Displays a social media post styled like a Facebook post
 */
const SocialPostCard = ({ post, articleSlug, postNumber }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(post.post_content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadImage = () => {
    if (post.image?.s3_url) {
      const filename = `${articleSlug}-social-${postNumber}.png`;
      const downloadUrl = `/api/tools/article/download?url=${encodeURIComponent(post.image.s3_url)}&filename=${encodeURIComponent(filename)}`;
      window.location.href = downloadUrl;
    }
  };

  const angleColors = {
    key_insight: { bg: '#e8f0fe', text: '#1a73e8', label: 'Key Insight' },
    surprising_fact: { bg: '#fef7e0', text: '#b06000', label: 'Surprising Fact' },
    actionable_takeaway: { bg: '#e6f4ea', text: '#137333', label: 'Actionable Takeaway' }
  };

  const angle = angleColors[post.post_angle] || { bg: '#f1f3f5', text: '#495057', label: `Post ${postNumber}` };

  const isDryRun = post.image?.s3_url === 'DRY_RUN_PLACEHOLDER';
  const hasImage = post.image?.s3_url;

  const iconButtonStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    backgroundColor: '#f8f9fa',
    color: '#495057',
    border: '1px solid #dee2e6',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  };

  return (
    <div>
      {/* Badge and action buttons on same line */}
      <div style={{ marginBottom: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span
          style={{
            display: 'inline-block',
            padding: '0.375rem 0.75rem',
            backgroundColor: angle.bg,
            color: angle.text,
            borderRadius: '4px',
            fontSize: '0.8rem',
            fontWeight: '600'
          }}
        >
          {post.post_angle_label || angle.label}
        </span>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            onClick={handleCopy}
            style={{
              ...iconButtonStyle,
              backgroundColor: copied ? '#198754' : '#f8f9fa',
              color: copied ? '#fff' : '#495057'
            }}
            title={copied ? 'Copied!' : 'Copy text'}
          >
            <i className={`bi ${copied ? 'bi-check' : 'bi-files'}`}></i>
          </button>
          {hasImage && (
            <button
              onClick={isDryRun ? undefined : handleDownloadImage}
              disabled={isDryRun}
              style={{
                ...iconButtonStyle,
                opacity: isDryRun ? 0.5 : 1,
                cursor: isDryRun ? 'not-allowed' : 'pointer'
              }}
              title={isDryRun ? 'Image not available in test mode' : 'Download image'}
            >
              <i className="bi bi-download"></i>
            </button>
          )}
        </div>
      </div>

      {/* Facebook-style card */}
      <div style={{
        border: '1px solid #dddfe2',
        borderRadius: '8px',
        backgroundColor: '#fff',
        overflow: 'hidden'
      }}>
        {/* Header with profile */}
        <div style={{
          padding: '12px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}>
          <img
            src="/images/elliot-koss.jpeg"
            alt="Profile"
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              objectFit: 'cover'
            }}
          />
          <div>
            <div style={{ fontWeight: '600', fontSize: '0.9375rem', color: '#050505' }}>
              Elliot Koss
            </div>
            <div style={{ fontSize: '0.75rem', color: '#65676b' }}>
              Just now · <i className="bi bi-globe2"></i>
            </div>
          </div>
        </div>

        {/* Post content */}
        <div style={{ padding: '0 16px 12px', fontSize: '0.9375rem', lineHeight: '1.5', color: '#050505', whiteSpace: 'pre-line' }}>
          {post.post_content}
        </div>

        {/* Image */}
        {post.image?.s3_url && post.image.s3_url !== 'DRY_RUN_PLACEHOLDER' ? (
          <img
            src={post.image.s3_url}
            alt={post.image.alt_text || `Social post ${postNumber} image`}
            style={{ width: '100%', display: 'block' }}
          />
        ) : post.image?.s3_url === 'DRY_RUN_PLACEHOLDER' && (
          <div style={{
            width: '100%',
            aspectRatio: '1200/630',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontSize: '1rem',
            fontWeight: '500'
          }}>
            <div style={{ textAlign: 'center', padding: '1rem' }}>
              <i className="bi bi-image" style={{ fontSize: '2rem', display: 'block', marginBottom: '0.5rem' }}></i>
              AI-Generated Image
            </div>
          </div>
        )}

        {/* Fake engagement bar */}
        <div style={{
          padding: '10px 16px',
          borderTop: '1px solid #dddfe2',
          display: 'flex',
          justifyContent: 'space-around',
          color: '#65676b',
          fontSize: '0.875rem'
        }}>
          <span><i className="bi bi-hand-thumbs-up"></i> Like</span>
          <span><i className="bi bi-chat"></i> Comment</span>
          <span><i className="bi bi-share"></i> Share</span>
        </div>
      </div>

    </div>
  );
};

export default SocialPostCard;
